import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    LOCALE_ID,
    OnInit,
    ViewEncapsulation,
    computed,
    inject,
    input,
    model,
    signal,
} from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { CerrixInputComponent } from "@app/shared/atoms/cerrix-input/cerrix-input.component";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixInputValidationCannotBeBeforeDateDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-cannot-be-before.directive";
import { CerrixInputValidationErrorsDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-errors.directive";
import { formatIsoDate } from "@methods/CommonMethods";

const DEFAULT_MIN = "1970-01-01";
const DEFAULT_MAX = "9999-12-31";
const FALLBACK_FORMAT = "dd/MM/yyyy";
const YEAR_DIGITS = 4;
const YEARS_PER_PAGE = 24;

// The capture group makes the same expression usable for split() and replace()
const FORMAT_TOKEN = /(dd|MM|yyyy)/g;

const TOKEN_FIELD: Record<string, DateField | undefined> = {
    dd: "day",
    MM: "month",
    yyyy: "year",
};

const MASK_DIGIT = "#";

type DayPosition = "previous" | "current" | "next";
type DateField = "day" | "month" | "year";
type DatepickerView = "day" | "month" | "year";

// One run of digit positions in the mask, as indexes into the typed digits
interface MaskSegment {
    start: number;
    end: number;
    field: DateField;
}

// Everything the format dictates, derived in one pass: the field order to parse against, the
// characters the mask may hold, and the slots the typed digits flow into
interface CompiledFormat {
    format: string;
    fields: DateField[];
    literals: Set<string>;
    slots: string[];
    segments: MaskSegment[];
}

interface DayCell {
    key: string;
    day: number;
    month: number;
    year: number;
    position: DayPosition;
    isDisabled: boolean;
}

interface MonthCell {
    key: string;
    month: number;
    label: string;
    isDisabled: boolean;
}

interface YearCell {
    key: string;
    year: number;
    isDisabled: boolean;
}

@Component({
    selector: "app-cerrix-datepicker-group",
    templateUrl: "./cerrix-datepicker-group.component.html",
    styleUrl: "./cerrix-datepicker-group.component.scss",
    imports: [
        MatIconModule,
        CerrixLabelComponent,
        CerrixInputComponent,
        CerrixInputValidationErrorsDirective,
        CerrixInputValidationCannotBeBeforeDateDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        // mousedown, not click: a header click (day -> month -> year) swaps the clicked
        // button out of the DOM via change detection before the click event finishes
        // bubbling, so by the time a document click handler saw it, the target looked
        // detached and this closed the picker on its own header click. mousedown fires
        // before that swap, so the target is still reliably inside the host here.
        "(document:mousedown)": "onDocumentMouseDown($event)",
        "(document:keydown.escape)": "closePicker()",
    },
})
export class CerrixDatepickerGroupComponent implements OnInit {
    readonly placeholder = input<string>("");
    readonly required = input<boolean>(false);
    readonly disabled = input<boolean>(false);
    readonly name = input<string>("");
    readonly value = model<string>("");
    readonly min = input<string>(DEFAULT_MIN);
    readonly max = input<string>(DEFAULT_MAX);
    readonly mustBeBefore = input<string>("");
    readonly mustBeAfter = input<string>("");
    readonly beforeErrorMessage = input<string>("");
    readonly afterErrorMessage = input<string>("");

    readonly format = input<string>("");

    readonly mask = input<boolean>(true);

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly locale = inject(LOCALE_ID);
    private readonly localeInfo = compileLocale(this.locale);
    private readonly monthFormatter = new Intl.DateTimeFormat(this.locale, { month: "long" });
    private readonly shortMonthFormatter = new Intl.DateTimeFormat(this.locale, { month: "short" });
    private readonly today = new Date();

    protected readonly shortDays = this.localeInfo.weekdayNames;
    protected readonly isOpen = signal(false);
    protected readonly todayKey = signal(formatIsoDate(new Date()));
    protected readonly view = signal<DatepickerView>("day");

    // Raw text while the user is typing; null means "show what value() holds"
    private readonly draft = signal<string | null>(null);
    protected readonly viewYear = signal(this.today.getFullYear());
    protected readonly viewMonth = signal(this.today.getMonth());
    protected readonly yearPageStart = signal(yearPageStartFor(this.today.getFullYear()));

    private readonly pattern = computed(() =>
        compileFormat(this.format() || this.localeInfo.format),
    );

    private readonly minDate = computed(
        () => parseIsoDate(this.min()) ?? (parseIsoDate(DEFAULT_MIN) as Date),
    );
    private readonly maxDate = computed(
        () => parseIsoDate(this.max()) ?? (parseIsoDate(DEFAULT_MAX) as Date),
    );
    protected readonly monthLabel = computed(() =>
        this.monthFormatter.format(new Date(this.viewYear(), this.viewMonth(), 1)),
    );

    protected readonly displayValue = computed(() => {
        const draft = this.draft();
        if (draft !== null) {
            return draft;
        }

        const selected = parseIsoDate(this.value());
        return selected ? renderDate(this.pattern().format, selected) : "";
    });

    protected readonly canGoPrevious = computed(
        () => new Date(this.viewYear(), this.viewMonth(), 0).getTime() >= this.minDate().getTime(),
    );

    protected readonly canGoNext = computed(
        () =>
            new Date(this.viewYear(), this.viewMonth() + 1, 1).getTime() <=
            this.maxDate().getTime(),
    );

    protected readonly days = computed<DayCell[]>(() => {
        const year = this.viewYear();
        const month = this.viewMonth();

        const leading =
            (new Date(year, month, 1).getDay() - this.localeInfo.firstDayOfWeek + 7) % 7;
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const trailing = (7 - ((leading + daysInMonth) % 7)) % 7;

        const cells: DayCell[] = [];
        for (let offset = -leading; offset < daysInMonth + trailing; offset++) {
            const date = new Date(year, month, 1 + offset);
            cells.push({
                key: formatIsoDate(date),
                day: date.getDate(),
                month: date.getMonth(),
                year: date.getFullYear(),
                position: offset < 0 ? "previous" : offset < daysInMonth ? "current" : "next",
                isDisabled: !this.isWithinBounds(date),
            });
        }
        return cells;
    });

    protected readonly months = computed<MonthCell[]>(() => {
        const year = this.viewYear();
        return Array.from({ length: 12 }, (_, month) => ({
            key: monthKey(year, month),
            month,
            label: this.shortMonthFormatter.format(new Date(year, month, 1)),
            isDisabled: !this.isMonthWithinBounds(year, month),
        }));
    });

    protected readonly years = computed<YearCell[]>(() => {
        const start = this.yearPageStart();
        return Array.from({ length: YEARS_PER_PAGE }, (_, offset) => {
            const year = start + offset;
            return {
                key: String(year),
                year,
                isDisabled: !this.isYearWithinBounds(year),
            };
        });
    });

    protected readonly yearPageLabel = computed(() => {
        const start = this.yearPageStart();
        return `${start} - ${start + YEARS_PER_PAGE - 1}`;
    });

    protected readonly currentMonthKey = computed(() => {
        const date = parseIsoDate(this.todayKey());
        return date ? monthKey(date.getFullYear(), date.getMonth()) : null;
    });

    protected readonly selectedMonthKey = computed(() => {
        const selected = parseIsoDate(this.value());
        return selected ? monthKey(selected.getFullYear(), selected.getMonth()) : null;
    });

    ngOnInit(): void {
        const selected = parseIsoDate(this.value());
        if (selected) {
            this.showMonthOf(selected);
        }
    }

    protected onInputTyped(event: Event): void {
        const draft = this.readField(event.target as HTMLInputElement, event);
        this.draft.set(draft);

        if (draft === "") {
            this.value.set(null);
        }

        const typed = parseFormatted(draft, this.pattern(), true);
        if (typed && this.isWithinBounds(typed)) {
            this.showMonthOf(typed);
            this.value.set(formatIsoDate(typed));
        }
    }

    private readField(field: HTMLInputElement, event: Event): string {
        const pattern = this.pattern();
        if (!this.mask()) {
            return field.value;
        }

        if ((event as InputEvent).inputType === "insertFromPaste") {
            const pasted = parseFormatted(field.value, pattern, false);
            if (pasted) {
                const rendered = renderDate(pattern.format, pasted);
                return writeField(field, rendered, rendered.length);
            }
        }

        return maskField(field, pattern, this.displayValue(), event);
    }

    protected onInputBlur(): void {
        const draft = this.draft();
        this.draft.set(null);
        if (draft === null) {
            return;
        }

        if (!draft.trim()) {
            this.value.set("");
            return;
        }

        const entered = parseFormatted(draft, this.pattern(), false);
        if (!entered || !this.isWithinBounds(entered)) {
            return;
        }

        this.showMonthOf(entered);
        this.value.set(formatIsoDate(entered));
    }

    protected togglePicker(): void {
        if (this.isOpen()) {
            this.close();
            return;
        }

        this.todayKey.set(formatIsoDate(new Date()));
        this.isOpen.set(true);
    }

    protected closePicker(): void {
        this.close();
    }

    protected onDocumentMouseDown(event: MouseEvent): void {
        if (!this.isOpen() || this.host.nativeElement.contains(event.target as Node)) {
            return;
        }
        this.close();
    }

    protected shiftMonth(delta: number): void {
        const shifted = new Date(this.viewYear(), this.viewMonth() + delta, 1);
        this.viewYear.set(shifted.getFullYear());
        this.viewMonth.set(shifted.getMonth());
    }

    protected selectDate(cell: DayCell): void {
        if (cell.isDisabled) {
            return;
        }

        this.draft.set(null);
        this.viewYear.set(cell.year);
        this.viewMonth.set(cell.month);
        this.value.set(cell.key);
        this.close();
    }

    protected openMonthView(): void {
        this.view.set("month");
    }

    protected selectMonth(cell: MonthCell): void {
        if (cell.isDisabled) {
            return;
        }

        this.viewMonth.set(cell.month);
        this.view.set("day");
    }

    protected openYearView(): void {
        this.yearPageStart.set(yearPageStartFor(this.viewYear()));
        this.view.set("year");
    }

    protected shiftYearPage(delta: number): void {
        this.yearPageStart.update((start) => start + delta * YEARS_PER_PAGE);
    }

    protected selectYear(cell: YearCell): void {
        if (cell.isDisabled) {
            return;
        }

        this.viewYear.set(cell.year);
        this.view.set("month");
    }

    private showMonthOf(date: Date): void {
        this.viewYear.set(date.getFullYear());
        this.viewMonth.set(date.getMonth());
    }

    private isWithinBounds(date: Date): boolean {
        return this.isRangeWithinBounds(date, date);
    }

    private isMonthWithinBounds(year: number, month: number): boolean {
        return this.isRangeWithinBounds(new Date(year, month, 1), new Date(year, month + 1, 0));
    }

    private isYearWithinBounds(year: number): boolean {
        return this.isRangeWithinBounds(new Date(year, 0, 1), new Date(year, 11, 31));
    }

    private isRangeWithinBounds(start: Date, end: Date): boolean {
        return (
            end.getTime() >= this.minDate().getTime() && start.getTime() <= this.maxDate().getTime()
        );
    }

    private close(): void {
        this.isOpen.set(false);
        this.view.set("day");
    }
}

function monthKey(year: number, month: number): string {
    return `${year}-${month}`;
}

// Aligns a page so its 24 years always fall on the same fixed boundaries, no matter which
// year the page is opened from
function yearPageStartFor(year: number): number {
    return year - (((year % YEARS_PER_PAGE) + YEARS_PER_PAGE) % YEARS_PER_PAGE);
}

function parseIsoDate(value: string): Date | null {
    const parts = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!parts) {
        return null;
    }

    const year = Number(parts[1]);
    const month = Number(parts[2]) - 1;
    const day = Number(parts[3]);
    const date = new Date(year, month, day);

    const overflowed = date.getMonth() !== month || date.getDate() !== day;
    return overflowed ? null : date;
}

function compileLocale(locale: string): {
    format: string;
    firstDayOfWeek: number;
    weekdayNames: string[];
} {
    type LocaleWithWeekInfo = Intl.Locale & { getWeekInfo?: () => { firstDay: number } };

    let format = FALLBACK_FORMAT;
    let firstDayOfWeek = 1;

    try {
        const parts = new Intl.DateTimeFormat(locale, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).formatToParts(new Date(2000, 0, 2));

        const types = new Set(parts.map((part) => part.type));
        if (types.has("day") && types.has("month") && types.has("year")) {
            format = parts
                .map((part) => {
                    switch (part.type) {
                        case "day":
                            return "dd";
                        case "month":
                            return "MM";
                        case "year":
                            return "yyyy";
                        default:
                            return part.value;
                    }
                })
                .join("");
        }

        // getWeekInfo() is not available in every engine
        firstDayOfWeek =
            ((new Intl.Locale(locale) as LocaleWithWeekInfo).getWeekInfo?.().firstDay ?? 1) % 7;
    } catch {
        // A locale Intl rejects keeps the fallback format and ISO-8601 (monday) week start
    }

    const weekdayFormatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
    const weekdayNames = Array.from({ length: 7 }, (_, index) =>
        weekdayFormatter
            .format(new Date(2024, 0, 7 + ((firstDayOfWeek + index) % 7)))
            .toUpperCase(),
    );

    return { format, firstDayOfWeek, weekdayNames };
}

function compileFormat(format: string): CompiledFormat {
    const fields: DateField[] = [];
    const literals = new Set<string>();
    const slots: string[] = [];
    const segments: MaskSegment[] = [];
    let digits = 0;

    for (const part of format.split(FORMAT_TOKEN)) {
        const field = TOKEN_FIELD[part];
        if (!field) {
            for (const char of part) {
                literals.add(char);
            }
            slots.push(...part);
            continue;
        }

        fields.push(field);
        segments.push({ start: digits, end: digits + part.length, field });
        slots.push(...MASK_DIGIT.repeat(part.length));
        digits += part.length;
    }

    return { format, fields, literals, slots, segments };
}

function maskField(
    field: HTMLInputElement,
    pattern: CompiledFormat,
    current: string,
    event: Event,
): string {
    const raw = field.value;
    const caret = field.selectionStart ?? raw.length;
    const inputType = (event as InputEvent).inputType;

    let digits = raw.replace(/\D/g, "");
    let digitsBefore = raw.slice(0, caret).replace(/\D/g, "").length;

    const inserted = (event as InputEvent).data;
    if (
        inputType === "insertText" &&
        inserted &&
        pattern.literals.has(inserted) &&
        digitsBefore === digits.length
    ) {
        const segment = pattern.segments.find((candidate) => candidate.end > digitsBefore);
        if (segment && segment.field !== "year" && digitsBefore > segment.start) {
            digits =
                digits.slice(0, segment.start) +
                "0".repeat(segment.end - digitsBefore) +
                digits.slice(segment.start);
            digitsBefore = segment.end;
        }
    }

    if (applyMask(digits, pattern.slots, digitsBefore).text === current) {
        if (inputType === "deleteContentBackward" && digitsBefore > 0) {
            digits = digits.slice(0, digitsBefore - 1) + digits.slice(digitsBefore);
            digitsBefore--;
        } else if (inputType === "deleteContentForward" && digitsBefore < digits.length) {
            digits = digits.slice(0, digitsBefore) + digits.slice(digitsBefore + 1);
        }
    }

    const masked = applyMask(digits, pattern.slots, digitsBefore);
    return writeField(field, masked.text, masked.caret);
}

function writeField(field: HTMLInputElement, text: string, caret: number): string {
    if (field.value !== text) {
        field.value = text;
    }
    field.setSelectionRange(caret, caret);
    return text;
}

function applyMask(
    digits: string,
    slots: string[],
    caretDigits: number,
): { text: string; caret: number } {
    let text = "";
    let caret = 0;
    let index = 0;

    for (const slot of slots) {
        if (index >= digits.length) {
            break;
        }
        text += slot === MASK_DIGIT ? digits[index++] : slot;
        if (index <= caretDigits) {
            caret = text.length;
        }
    }

    return { text, caret };
}

function renderDate(format: string, date: Date): string {
    return format.replace(FORMAT_TOKEN, (token) => {
        switch (token) {
            case "dd":
                return String(date.getDate()).padStart(2, "0");
            case "MM":
                return String(date.getMonth() + 1).padStart(2, "0");
            default:
                return String(date.getFullYear()).padStart(YEAR_DIGITS, "0");
        }
    });
}

function parseFormatted(
    text: string,
    pattern: CompiledFormat,
    requireFullYear: boolean,
): Date | null {
    const chunks = text.split(/\D+/).filter(Boolean);
    if (chunks.length !== pattern.fields.length) {
        return null;
    }

    const entered: Record<DateField, number> = { day: NaN, month: NaN, year: NaN };
    let yearDigits = 0;
    pattern.fields.forEach((field, index) => {
        entered[field] = Number(chunks[index]);
        if (field === "year") {
            yearDigits = chunks[index].length;
        }
    });

    if (requireFullYear && yearDigits !== YEAR_DIGITS) {
        return null;
    }

    const month = entered.month - 1;
    const date = new Date(entered.year, month, entered.day);

    const overflowed =
        date.getFullYear() !== entered.year ||
        date.getMonth() !== month ||
        date.getDate() !== entered.day;
    return overflowed ? null : date;
}
